import { FormQuestionDto, FormSectionDto, AnswerType } from '../onsite-visit.dto';

/**
 * Validates a single question based on its configuration and answer type
 * @param question - The question to validate
 * @param response - The user's response value
 * @param questionEntityMap - Map of question IDs to selected entity IDs
 * @param skipEntityValidation - Skip entity selection validation (for questions inside sections)
 * @returns true if the question has validation errors, false otherwise
 */
export function validateQuestion(
    question: FormQuestionDto,
    response: string,
    questionEntityMap: Record<string, string[]>,
    skipEntityValidation: boolean = false,
): boolean {
    const isEntityTarget = question.targetEntity === 'Building';
    const entities = questionEntityMap[question.id] || [];
    const validation = question.config?.validation;
    let hasError = false;

    // Validate entity selection if entity-targeted (only for standalone questions)
    if (!skipEntityValidation && isEntityTarget && entities.length === 0) {
        hasError = true;
    }

    // Validate required field
    if (question.config?.required && !response.trim()) {
        hasError = true;
    }

    // Skip further validation if empty and not required
    if (!response && !question.config?.required) {
        return hasError;
    }

    // Validate NUMBER type: min/max range
    if (question.answerType === AnswerType.NUMBER && validation) {
        const num = parseFloat(response);
        if (isNaN(num)) {
            hasError = true;
        } else {
            if (validation.min !== null && num < validation.min) {
                hasError = true;
            }
            if (validation.max !== null && num > validation.max) {
                hasError = true;
            }
        }
    }

    // Validate TEXT type: regex pattern
    if (question.answerType === AnswerType.TEXT && validation?.regex) {
        try {
            const regex = new RegExp(validation.regex);
            if (!regex.test(response)) {
                hasError = true;
            }
        } catch (e) {
            // Invalid regex pattern - log but don't fail validation
            console.warn(`Invalid regex pattern for question ${question.id}:`, validation.regex);
        }
    }

    // Validate DATE_TIME type: date range
    if (question.answerType === AnswerType.DATE_TIME && validation?.dateRange) {
        try {
            const date = new Date(response);
            const { from, to } = validation.dateRange;

            if (from && date < new Date(from)) {
                hasError = true;
            }
            if (to && date > new Date(to)) {
                hasError = true;
            }
        } catch (e) {
            // Invalid date format
            hasError = true;
        }
    }

    return hasError;
}

/**
 * Validates a section and all its instances
 * @param section - The section to validate
 * @param responses - Map of question IDs to response values
 * @param instanceEntityMap - Map of instance keys to selected entity IDs
 * @param errors - Record to populate with error states (mutated)
 * @returns true if the section has validation errors, false otherwise
 */
export function validateSection(
    section: FormSectionDto,
    responses: Record<string, string>,
    instanceEntityMap: Record<string, string[]>,
    errors: Record<string, boolean>,
): boolean {
    const sectionKey = `section-${section.sectionIdentifier}`;
    let hasError = false;

    // Validate each instance has entity selection
    section.instances.forEach((instance, instanceIndex) => {
        const instanceKey = `${sectionKey}-instance-${instanceIndex}`;

        // Check if section needs entity selection at instance level
        if (section.targetEntity === 'Building') {
            const entities = instanceEntityMap[instanceKey] || [];
            if (entities.length === 0) {
                errors[instanceKey] = true;
                hasError = true;
            } else {
                // Clear error if entities are selected
                errors[instanceKey] = false;
            }
        }

        // Validate all questions within instance
        instance.forEach((question) => {
            // Check both user input and prefilled response
            const response = responses[question.id] || question.response || '';
            // Questions inside sections don't validate entity selection
            // The section instance already handles building selection
            const questionHasError = validateQuestion(question, response, {}, true);

            if (questionHasError) {
                errors[question.id] = true;
                hasError = true;
            } else {
                // Clear error if question is valid
                errors[question.id] = false;
            }
        });
    });

    return hasError;
}

/**
 * Validates all form items (questions and sections)
 * @param items - Array of form items to validate
 * @param responses - Map of question IDs to response values
 * @param questionEntityMap - Map of question IDs to selected entity IDs
 * @param instanceEntityMap - Map of instance keys to selected entity IDs
 * @returns Object containing validation errors and hasErrors flag
 */
export function validateFormItems(
    items: (FormQuestionDto | FormSectionDto)[],
    responses: Record<string, string>,
    questionEntityMap: Record<string, string[]>,
    instanceEntityMap: Record<string, string[]>,
): { errors: Record<string, boolean>; hasErrors: boolean } {
    let hasErrors = false;
    const errors: Record<string, boolean> = {};

    items.forEach((item) => {
        if (item.type === 'question') {
            const question = item as FormQuestionDto;
            // Check both user input and prefilled response
            const response = responses[question.id] || question.response || '';
            const questionHasError = validateQuestion(question, response, questionEntityMap);

            if (questionHasError) {
                errors[question.id] = true;
                hasErrors = true;
            } else {
                // Clear error if question is valid
                errors[question.id] = false;
            }
        } else if (item.type === 'section') {
            const section = item as FormSectionDto;
            const sectionHasError = validateSection(section, responses, instanceEntityMap, errors);
            hasErrors = hasErrors || sectionHasError;
        }
    });

    return { errors, hasErrors };
}
